import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ZCDIcon, ZCDSelect } from '@zencity/common-ui';
import QuestionResults from '../QuestionResults';

function MultiPagesDisplay(props) {
  const { sections, engagementSummary, introduction, conclusion, createdAt } = props;
  const ref = useRef(null);

  const pages = useMemo(() => {
    const pagesArr = [];

    if (introduction) pagesArr.push(introduction);
    sections.forEach((section) => {
      section.questions.forEach((question) => {
        pagesArr.push(
          <div key={question.uuid} className="results-summary__question-results no-border">
            <h2 className="results-summary__question-results__title title is-5 is-primary-color">
              {question.title}
            </h2>
            <QuestionResults
              question={question}
              engagementSummary={engagementSummary}
              createdAt={createdAt}
            />
          </div>
        );
      });
    });
    if (conclusion) pagesArr.push(conclusion);

    return pagesArr;
  }, [sections, introduction, conclusion]);

  const options = useMemo(() => {
    const titles = [];

    if (introduction) titles.push('Introduction');
    sections.forEach((section) =>
      section.questions.forEach((question) => titles.push(question.title))
    );
    if (conclusion) titles.push('Conclusion');

    return titles.map((title, index) => ({ label: title, value: index }));
  }, [introduction, sections, conclusion]);

  const [pageIndex, setPageIndex] = useState(0);
  const disableBackButton = useMemo(() => pageIndex <= 0, [pageIndex]);
  const disableNextButton = useMemo(() => pageIndex >= pages.length - 1, [pageIndex]);

  const scrollTo = () => {
    if (!ref.current) return;

    const bodyRect = document.body.getBoundingClientRect();
    const elemRect = ref.current.getBoundingClientRect();
    const offset = elemRect.top - bodyRect.top;
    window.scrollTo(0, offset);
  };

  const goBack = () => {
    if (pageIndex <= 0) return;

    setPageIndex(pageIndex - 1);
    scrollTo();
  };

  const goNext = () => {
    if (pageIndex > pages.length - 1) return;

    setPageIndex(pageIndex + 1);
    scrollTo();
  };

  const jumpTo = (newValue) => {
    setPageIndex(newValue.value);
    scrollTo();
  };

  return (
    <div ref={ref} className="results-summary-multi-pages-display">
      {pages[pageIndex]}
      <div className="results-summary-multi-pages-display__navigation">
        <div className="results-summary-multi-pages-display__navigation__buttons">
          <button
            type="button"
            className="button is-primary is-primary-background"
            disabled={disableBackButton}
            onClick={goBack}
          >
            <ZCDIcon color="white" name="arrow-left" /> <span>Back</span>
          </button>
          <button
            type="button"
            className="button is-primary is-primary-background"
            disabled={disableNextButton}
            onClick={goNext}
          >
            <span>Next</span>
            <ZCDIcon color="white" name="arrow-right" />{' '}
          </button>
        </div>
        <div className="results-summary-multi-pages-display__navigation__selector">
          <ZCDSelect
            options={options}
            value={options[pageIndex]}
            onChange={jumpTo}
            placeholder="Go to page..."
          />
        </div>
      </div>
    </div>
  );
}

MultiPagesDisplay.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      questions: PropTypes.arrayOf(
        PropTypes.shape({
          uuid: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          type: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  engagementSummary: PropTypes.shape({}).isRequired,
  introduction: PropTypes.element,
  conclusion: PropTypes.element,
  createdAt: PropTypes.string.isRequired,
};

MultiPagesDisplay.defaultProps = {
  introduction: null,
  conclusion: null,
};

export default MultiPagesDisplay;
